/**
 * DOM-free rules behind the Review step's one-person test side panel (plan
 * 2026-10-15-003 U3, §4.3 and §6). Zero Glaze/React imports so `bun:test` can
 * import it directly (same split as `review-step-logic.ts`).
 *
 * Every sentence here is rep-facing (R20): no ids, status codes or raw error
 * text. Rep-facing STATUS words for a running/finished test still come from
 * `classifyRepStatus` — this file only decides "can I press Test" and "what do
 * I say when the request itself failed".
 */
import { MiniAppFetchError, WriteRefusedError } from '../_shared/fetch-error';

import { PLAYBOOKS_TEST_SEND_MAX_MEMBERS, type PlaybooksDispatchLogRow, type PlaybooksPlaySendsResponse } from './schemas';

export interface MemberTestDisabledInput {
  /** `false` in Playbook mode (no `playId`). */
  hasPlay: boolean;
  /** `false` for a person in "Not in any group". */
  inGroup: boolean;
  /** Whether the person's group has a sequence mapped. */
  rungMapped: boolean;
  /** `notReadyBadgeText(...)` when the sequence is not ready, else `null`. */
  notReadyText: string | null;
  /** `false` while the ladder is loading or stale (`tally === null`). */
  tallyAvailable: boolean;
  /** `reviewActionsLocked(pendingAction)`. */
  actionsLocked: boolean;
}

/** `null` when a test can be sent, else the one sentence that says why not. */
export function memberTestDisabledReason(input: MemberTestDisabledInput): string | null {
  if (!input.hasPlay) return "To send a test, open this playbook from a campaign's Play.";
  if (!input.inGroup) return "This person doesn't match any group, so no sequence applies.";
  if (!input.rungMapped) return "This group has no sequence, so there's nothing to test.";
  if (!input.tallyAvailable) return 'Matching members to each group — try again in a moment.';
  if (input.actionsLocked) return 'Finish the send above first.';
  if (input.notReadyText !== null) {
    const base = input.notReadyText.trimEnd().replace(/\.$/, '');
    return `${base}. Fix it on the Sequences step, then test.`;
  }
  return null;
}

/** The browser gave up waiting; the server may well have accepted the test. */
export const MEMBER_TEST_STILL_STARTING_SENTENCE =
  'Terra is still starting this test — it will appear here in a moment.';

/** Anything the server did not explain in words. */
export const MEMBER_TEST_FALLBACK_SENTENCE = "Couldn't start the test. Try again in a moment.";

export interface MemberTestFailure {
  content: string;
  /** `'info'` when the test may be starting; `'error'` when it was refused. */
  status: 'error' | 'info';
  /** After refetching sends, adopt the newest test dispatch IF its one row is this person's. */
  adoptNewestTest: boolean;
}

function isAbortLike(error: unknown): boolean {
  if (typeof error !== 'object' || error === null || !('name' in error)) return false;
  const { name } = error as { name?: unknown };
  return name === 'AbortError' || name === 'TimeoutError';
}

/**
 * Words a failed test request (built on `sendFailureOf`'s shape). Returns
 * `null` for a `WriteRefusedError` — core already toasted it, say nothing more.
 */
export function memberTestFailureOf(error: unknown): MemberTestFailure | null {
  if (error instanceof WriteRefusedError) return null;
  if (isAbortLike(error)) {
    return { content: MEMBER_TEST_STILL_STARTING_SENTENCE, status: 'info', adoptNewestTest: true };
  }
  if (error instanceof MiniAppFetchError && error.status < 500 && error.userMessage) {
    return { content: error.userMessage, status: 'error', adoptNewestTest: false };
  }
  return { content: MEMBER_TEST_FALLBACK_SENTENCE, status: 'error', adoptNewestTest: false };
}

/**
 * The name a rep reads in "Send a test for {name}". The first word of a
 * "First Last" name; the FULL name when it has a comma ("Last, First" —
 * guessing which word is the first name would greet the surname); a neutral
 * "this person" when there is no name (never an id).
 */
export function memberFirstName(name: string | null | undefined): string {
  const trimmed = (name ?? '').trim();
  if (trimmed === '') return 'this person';
  if (trimmed.includes(',')) return trimmed;
  return trimmed.split(/\s+/)[0] ?? 'this person';
}

/**
 * Is this person's latest test still going? Answered from the sends list the
 * Dispatch log already polls — never a per-person fetch, so a row button costs
 * no query (plan 2026-10-15-004 §4.2). A recorded test the list does not show
 * yet was just started, so it counts as working. For a one-person test
 * `summary.live` is true exactly while `classifyRepStatus` says in progress.
 */
export function memberTestWorking(
  latest: { dispatchId: string } | undefined,
  sends: Pick<PlaybooksPlaySendsResponse, 'testSends'> | undefined,
): boolean {
  if (latest === undefined) return false;
  const entry = sends?.testSends.find((t) => t.dispatchId === latest.dispatchId);
  return entry === undefined ? true : entry.summary.live;
}

// ── "Test selected": several ticked people, one test (Review folders) ──

/** The most people one "Test selected" press sends — the server's own chosen-people limit, so the two cannot drift. */
export const MEMBER_TEST_BATCH_MAX = PLAYBOOKS_TEST_SEND_MAX_MEMBERS;

/**
 * Who a "Test selected" press sends: the ticked people in the FOLDER's order
 * (never the order they were ticked), at most {@link MEMBER_TEST_BATCH_MAX}.
 * `selectedCount` is how many are ticked in this folder (a tick on someone no
 * longer in the folder does not count).
 */
export function pickTestBatch(
  folderOrder: readonly string[],
  selected: ReadonlySet<string>,
): { ids: string[]; selectedCount: number } {
  const inFolder = folderOrder.filter((id) => selected.has(id));
  return { ids: inFolder.slice(0, MEMBER_TEST_BATCH_MAX), selectedCount: inFolder.length };
}

/** Said next to the button when more are ticked than one press sends; otherwise `null`. */
export function testBatchNote(selectedCount: number): string | null {
  if (selectedCount <= MEMBER_TEST_BATCH_MAX) return null;
  return `Testing the first ${MEMBER_TEST_BATCH_MAX} of ${selectedCount.toLocaleString('en-US')} selected.`;
}

/**
 * One person's row of a test dispatch. A "Test selected" batch is ONE dispatch
 * of up to {@link MEMBER_TEST_BATCH_MAX} rows (one page), recorded under every
 * ticked person, so `rows[0]` is only right for a one-person test. `undefined`
 * while the person's row is not listed yet — the caller treats that as "still
 * working", never as someone else's result.
 */
export function memberTestRowOf<T extends Pick<PlaybooksDispatchLogRow, 'campaign_member_id'>>(
  rows: readonly T[],
  memberId: string,
): T | undefined {
  return rows.find((row) => row.campaign_member_id === memberId);
}
